/** Message AST (server-parsed) → render model: styled text runs with mention / channel / agent chips. */
import type { Block, Channel, Inline, Member, UserGroup } from "@bundle/contracts";
import { groupIncludes, groupMentionLabel } from "./userGroups.ts";

/** `group`: a user group I'm not in (a group I'm in reads as `self`, highlighted like a mention of me, with `groupId`). */
export type ChipKind = "user" | "agent" | "channel" | "broadcast" | "self" | "group";

export type TextRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  link?: string;
  chip?: ChipKind;
  /** Target of a user/agent chip. */
  userId?: string;
  /** Target of a channel chip (absent for a private channel I can't see). */
  channelId?: string;
  /** A channel chip for a channel I can't see (Slack: "private channel", with a lock; not clickable). */
  privateChannel?: boolean;
  /** Target of a user group chip (opens the group card). */
  groupId?: string;
};

export type RenderBlock =
  | { type: "paragraph"; runs: TextRun[] }
  | { type: "code"; text: string; lang?: string }
  | { type: "quote"; blocks: RenderBlock[] }
  | { type: "list"; ordered: boolean; items: RenderBlock[][] };

export type RichTextContext = {
  meId: string;
  members: Record<string, Member>;
  channels: Record<string, Channel>;
  /**
   * What a mention of a channel I can't see reads as (private channels I'm not in aren't in `channels`), in the
   * reader's language. Absent ⇒ "private channel".
   */
  privateChannelLabel?: string;
  /** User groups by id (absent ⇒ none known: group mentions read as "@group"). */
  userGroups?: Record<string, UserGroup>;
};

/** A mention of a channel I can't see, when the context brings no translated label. */
export const PRIVATE_CHANNEL_LABEL = "private channel";

/** Reactions offered in the hover bar / long-press menu (content, not UI icons). */
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "👀"];

export function inlineRuns(nodes: Inline[], ctx: RichTextContext): TextRun[] {
  const runs: TextRun[] = [];
  for (const n of nodes) {
    switch (n.type) {
      case "text": {
        const r: TextRun = { text: n.text };
        for (const m of n.marks ?? []) r[m] = true;
        runs.push(r);
        break;
      }
      case "link":
        runs.push({ text: n.text === undefined || n.text === "" ? n.url : n.text, link: n.url });
        break;
      case "mention_user": {
        const m = ctx.members[n.userId];
        const chip: ChipKind = n.userId === ctx.meId ? "self" : m !== undefined && m.kind === "agent" ? "agent" : "user";
        runs.push({ text: `@${m === undefined ? n.userId : m.displayName}`, chip, userId: n.userId });
        break;
      }
      case "mention_channel": {
        const c = ctx.channels[n.channelId];
        if (c === undefined) runs.push({ text: ctx.privateChannelLabel ?? PRIVATE_CHANNEL_LABEL, chip: "channel", privateChannel: true });
        else runs.push({ text: `#${c.name}`, chip: "channel", channelId: n.channelId });
        break;
      }
      case "mention_broadcast":
        runs.push({ text: `@${n.range}`, chip: "broadcast" });
        break;
      case "mention_group": {
        const g = ctx.userGroups?.[n.groupId];
        runs.push({ text: groupMentionLabel(g), chip: groupIncludes(g, ctx.meId) ? "self" : "group", groupId: n.groupId });
        break;
      }
      case "emoji":
        runs.push({ text: n.unicode ?? `:${n.name}:` });
        break;
    }
  }
  return runs;
}

export function renderBlocks(blocks: Block[], ctx: RichTextContext): RenderBlock[] {
  return blocks.map((b): RenderBlock => {
    switch (b.type) {
      case "paragraph":
        return { type: "paragraph", runs: inlineRuns(b.children, ctx) };
      case "code_block":
        return b.lang === undefined ? { type: "code", text: b.text } : { type: "code", text: b.text, lang: b.lang };
      case "quote":
        return { type: "quote", blocks: renderBlocks(b.children, ctx) };
      case "list":
        return { type: "list", ordered: b.ordered, items: b.items.map((item) => renderBlocks(item, ctx)) };
    }
  });
}

/** True when a message is only 1–3 emoji (rendered larger). */
export function isEmojiOnly(blocks: Block[]): boolean {
  if (blocks.length !== 1) return false;
  const b = blocks[0]!;
  if (b.type !== "paragraph") return false;
  let count = 0;
  for (const n of b.children) {
    if (n.type === "emoji") count += 1;
    else if (n.type === "text") {
      const t = n.text.replace(/\s+/g, "");
      if (t === "") continue;
      if (!/^(\p{Extended_Pictographic}|\p{Emoji_Component}|‍|️)+$/u.test(t)) return false;
      count += [...new Intl.Segmenter("en", { granularity: "grapheme" }).segment(t)].length;
    } else return false;
  }
  return count > 0 && count <= 3;
}
